import React from "react";
import { useTheme } from "@mui/material";

import {
  Dialog,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  FormControlLabel,
  Checkbox,
  Divider,
  Typography,
  Box,
} from "@tiny/components";
import { useExportAudience } from "./ExportAudienceDialog.hooks";
import { ExportAudienceDialogProps } from "./ExportAudience.types";
import { attributeNameLabelMap } from "./ExportAudience.helpers";

function ExportAudienceDialog(props: ExportAudienceDialogProps) {
  const { open, onClose } = props;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <ExportAudienceDialogContent {...props} />
    </Dialog>
  );
}

function ExportAudienceDialogContent(props: ExportAudienceDialogProps) {
  const theme = useTheme();
  const {
    onClose,
    onSuccess,
    defaultColumns,
    allChecked,
    isSelected,
    selectAll,
    allOtherColumns,
    selectColumn,
    selected,
    customAttributeNames,
  } = useExportAudience(props);

  return (
    <>
      <DialogTitle onClose={onClose}>Export Audience</DialogTitle>
      <DialogContent>
        <Typography
          component="h2"
          variant="body2"
          fontWeight="regular"
          mt={0}
          color="grey"
          tone={700}
        >
          Default Column to be exported in file
        </Typography>
        <Grid container spacing={7} verticalSpacing={3}>
          {defaultColumns.map((column) => (
            <Grid key={column} item xs={12} md={6}>
              <FormControlLabel
                control={<Checkbox color="primary" />}
                label={attributeNameLabelMap[column]}
                checked={true}
              />
            </Grid>
          ))}
        </Grid>
        <Box
          mt={5}
          sx={{
            position: "relative",
          }}
        >
          <Typography
            component="h2"
            variant="body2"
            fontWeight="regular"
            color="grey"
            tone={700}
            sx={{
              position: "relative",
              width: 110,
              zIndex: 2,
              backgroundColor: "#fff",
            }}
          >
            Add other fields
          </Typography>
          <Divider
            style={{
              position: "absolute",
              top: "50%",
              left: 0,
              right: 0,
              zIndex: 1,
            }}
          />
        </Box>
        <Grid container spacing={7} verticalSpacing={3}>
          <Grid key={"All-fields"} item xs={12} md={12}>
            <FormControlLabel
              control={<Checkbox color="primary" />}
              label="All columns"
              checked={allChecked}
              onChange={(_event: React.ChangeEvent<{}>, checked) =>
                selectAll(checked)
              }
            />
          </Grid>
          {allOtherColumns.map((column) => {
            const isCustomAttr = customAttributeNames.includes(column);
            const label = attributeNameLabelMap[column] ?? column;
            return (
              <Grid key={column} item xs={12} md={6}>
                <FormControlLabel
                  control={<Checkbox color="primary" />}
                  label={
                    isCustomAttr ? (
                      <span style={{ color: theme.palette.secondary.main }}>
                        {label}
                      </span>
                    ) : (
                      label
                    )
                  }
                  checked={isSelected(column)}
                  onChange={(_event: React.ChangeEvent<{}>, checked) =>
                    selectColumn(column, checked)
                  }
                />
              </Grid>
            );
          })}
        </Grid>
        <Divider mt={4} />
      </DialogContent>
      <DialogActions>
        <Box
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
          }}
        >
          <Button
            variant="text"
            onClick={onClose}
            style={{
              paddingLeft: 0,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const columns_to_exported = [...defaultColumns, ...selected];
              onClose();
              return onSuccess("CSV", columns_to_exported);
            }}
          >
            Export
          </Button>
        </Box>
      </DialogActions>
    </>
  );
}

export default ExportAudienceDialog;
